import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, RotateCw, Download, RefreshCw } from 'lucide-react';
import Slider from '@mui/material/Slider';

interface ImageCropperProps {
  isOpen: boolean;
  onClose: () => void;
  onCropComplete: (croppedImageUrl: string) => void;
  imageUrl?: string;
  aspectRatio?: number;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  isOpen,
  onClose,
  onCropComplete,
  imageUrl,
  aspectRatio
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {

    const { width, height } = e.currentTarget;

    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 80,
        },
        aspectRatio || width / height,
        width,
        height
      ),
      width,
      height
    );

    setCrop(crop);
    setLoading(false);
  }, [aspectRatio]);

  const generateCroppedImage = useCallback(async () => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Pour la rotation, il faut ajuster la taille du canvas si l'angle est 90/270
    const angle = (rotate || 0) * Math.PI / 180;
    const cropWidth = completedCrop.width;
    const cropHeight = completedCrop.height;
    const rotated = Math.abs(Math.round((rotate || 0) / 90)) % 2 === 1;
    canvas.width = rotated ? cropHeight : cropWidth;
    canvas.height = rotated ? cropWidth : cropHeight;

    ctx.save();
    // Déplacer le contexte au centre du canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angle);

    // Décaler l'image pour que le crop soit centré
    // Si rotation 90/270, inverser width/height
    const drawWidth = cropWidth * scaleX;
    const drawHeight = cropHeight * scaleY;
    const sx = completedCrop.x * scaleX;
    const sy = completedCrop.y * scaleY;

    // Pour drawImage, il faut ajuster l'offset selon l'angle
    if (rotated) {
      ctx.drawImage(
        image,
        sx,
        sy,
        drawWidth,
        drawHeight,
        -canvas.height / 2,
        -canvas.width / 2,
        canvas.height,
        canvas.width
      );
    } else {
      ctx.drawImage(
        image,
        sx,
        sy,
        drawWidth,
        drawHeight,
        -canvas.width / 2,
        -canvas.height / 2,
        canvas.width,
        canvas.height
      );
    }
    ctx.restore();

    return canvas.toDataURL('image/jpeg', 0.9);
  }, [completedCrop, rotate]);

  const handleCropComplete = async () => {
    const croppedImageUrl = await generateCroppedImage();
    if (croppedImageUrl) {
      onCropComplete(croppedImageUrl);
    }
  };

  const resetCrop = () => {
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      const newCrop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 80,
          },
          aspectRatio || width / height,
          width,
          height
        ),
        width,
        height
      );
      setCrop(newCrop);
    }
    setScale(1);
    setRotate(0);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Controls */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-2" style={{ flex: 1}}>
          <label className="para-16 color-000000" style={{ minWidth: '50px' }}>Scale:</label>
          <Slider
            aria-label="Scale"
            value={scale}
            min={0.5}
            max={3}
            step={0.1}
            onChange={(e, value) => setScale(value as number)}
            sx={{ width: '100%', color: '#746AFF', marginRight: '10px' }}
          />
          <span className="para-14" style={{ minWidth: '45px' }}>{scale.toFixed(1)}x</span>
        </div>

        <div className="d-flex align-items-center gap-2" style={{ flex: 1, marginRight: '20px' }}>
          <label className="para-16 color-000000" style={{ minWidth: '60px' }}>Rotate:</label>
          <Slider
            aria-label="rotate"
            value={rotate}
            min={0}
            max={360}
            step={0.1}
            onChange={(e, value) => setRotate(value as number)}
            sx={{ width: '100%', color: '#746AFF', marginRight: '10px' }}
          />
          <span className="para-14" style={{ minWidth: '40px' }}>{rotate}°</span>
        </div>

        <button
          onClick={resetCrop}
          className="button-dash back-746AFF color-FFFFFF  border-746AFF padd-10"
        >
          Reset
        </button>
      </div>

      {/* Crop Area */}
      <div className="flex justify-center mb-6">
        {loading && (
          <div className="overlay d-flex justify-content-center align-items-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>

        )}
        <div className={`max-w-full max-h-96 overflow-hidden rounded-lg border-2 border-gray-200 ${loading ? 'opacity-0' : 'opacity-100'}`}>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspectRatio}
            minWidth={50}
            minHeight={50}
          >
            <img
              ref={imgRef}
              alt="Crop preview"
              src={imageUrl}
              crossOrigin="anonymous"
              style={{
                transform: `scale(${scale}) rotate(${rotate}deg)`,
                maxHeight: '400px',
                maxWidth: '100%'
              }}
              onLoad={onImageLoad}
              className="max-w-full h-auto"
            />
          </ReactCrop>
        </div>
      </div>

      {/* Preview Canvas (hidden) */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button
          onClick={onClose}
          className="button-dash back-238-242 color-746AFF  border-746AFF padd-15"
        >
          Cancel
        </button>
        <button
          onClick={handleCropComplete}
          className="button-dash back-746AFF color-FFFFFF  border-746AFF padd-15"
        >
          Apply Crop
        </button>
      </div>
    </>
  );
};

export default ImageCropper;